
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle, Star, AlertTriangle, ShieldAlert } from 'lucide-react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { isLicensedPractitioner } from '@/utils/practitioner-mode';
import { useLocalCurrency } from '@/hooks/useLocalCurrency';
import { getBilingualValue } from '@/utils/bilingual-helpers';

interface TherapyModality {
  id: string;
  name_cn: string;
  name_en: string;
}

interface ServiceTherapyTag {
  id: string;
  is_primary: boolean;
  therapy_modality: TherapyModality;
}

interface Service {
  id: string;
  service_name: string;
  service_name_en?: string | null;
  service_name_zh?: string | null;
  duration_minutes: number;
  price: number;
  currency: string;
  description?: string;
  description_en?: string | null;
  description_zh?: string | null;
  is_free_session?: boolean;
  formats_available?: string[];
  therapy_tags?: ServiceTherapyTag[];
  service_track?: 'clinical' | 'wellness';
  adults_only?: boolean;
  parental_consent_required?: boolean;
}

interface PractitionerServicesProps {
  services: Service[];
  selectedService: string | null;
  onServiceSelect: (serviceId: string | null) => void;
  onBookService: (serviceId: string) => void;
  practitionerId?: string;
  practitionerName?: string;
  professionalTitle?: string;
  avatarUrl?: string;
  practitionerSlug?: string;
  practitionerTimezone?: string;
  practiceMode?: 'licensed' | 'wellness' | null;
}

const PractitionerServices: React.FC<PractitionerServicesProps> = ({
  services,
  selectedService,
  onServiceSelect,
  onBookService,
  practitionerId,
  practitionerName,
  professionalTitle,
  avatarUrl,
  practitionerSlug,
  practitionerTimezone,
  practiceMode
}) => {
  const navigate = useNavigate();
  const { user, activeRole } = useAuth();
  const { toast } = useToast();
  const { t, i18n } = useTranslation('practitioners');
  const [showRoleAlert, setShowRoleAlert] = React.useState(false);
  // Determine per-service or practitioner-level licensed mode
  const isLicensedMode = isLicensedPractitioner(practiceMode);
  const { userCurrency, formatLocal, isLoading: ratesLoading } = useLocalCurrency();

  const handleBookNow = (service: Service) => {
    console.log("Current active role:", activeRole);

    // Check if user is not logged in
    if (!user) {
      toast({
        title: "Login Required",
        description: t('loginRequired'),
        variant: "default"
      });
      navigate('/client-auth');
      return;
    }

    // Check if user is practitioner or admin
    if (activeRole === 'practitioner' || activeRole === 'admin') {
      setShowRoleAlert(true);
      return;
    }

    // Check if client role
    if (activeRole === 'client') {
      // Validate required data
      if (!practitionerId || !practitionerName) {
        toast({
          variant: "destructive",
          title: "Missing practitioner information",
          description: t('selectPractitionerAgain')
        });
        return;
      }

      // Store temporary booking information for the date/time selection page
      const returnToPath = `/book-an-appointment/${practitionerSlug || practitionerId}`;
      const isServiceClinical = service.service_track === 'clinical';
      const tempBookingInfo = {
        practitionerId: practitionerId,
        practitionerName: practitionerName || 'Unknown Practitioner',
        serviceId: service.id,
        serviceName: service.service_name,
        price: isServiceClinical ? 0 : service.price,
        currency: service.currency,
        duration: service.duration_minutes,
        professionalTitle: professionalTitle,
        avatarUrl: avatarUrl,
        returnToPath,
        practitionerTimezone: practitionerTimezone || 'America/New_York',
        bookingMode: isServiceClinical ? 'request_only' : 'paid',
        adults_only: service.adults_only || false,
        parental_consent_required: service.parental_consent_required || false,
      };
      console.log("Storing temporary booking info with timezone:", tempBookingInfo);
      sessionStorage.setItem('tempBookingInfo', JSON.stringify(tempBookingInfo));

      // Also clear any existing bookingInfo to prevent conflicts
      sessionStorage.removeItem('bookingInfo');

      // Navigate to date/time selection page
      navigate('/booking-datetime');
    }
  };

  const handleGoToClientLogin = () => {
    navigate('/client-auth');
    setShowRoleAlert(false);
  };

  if (!services || services.length === 0) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t('availableServices')}</h3>
        <p className="text-gray-600">{t('noServicesAvailable')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t('availableServices')}</h3>
        <div className="space-y-3">
          {services.map((service) => (
            <Card 
              key={service.id} 
              className={`cursor-pointer transition-colors ${
                selectedService === service.id 
                  ? 'border-amber-500 bg-amber-50' 
                  : 'hover:border-amber-200'
              }`}
              onClick={() => onServiceSelect(service.id)}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-medium text-lg">{getBilingualValue(i18n.language, service.service_name_en, service.service_name_zh, service.service_name)}</h4>
                      {service.service_track === 'clinical' && (
                        <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs">
                          Clinical
                        </Badge>
                      )}
                      {selectedService === service.id && (
                        <CheckCircle className="h-5 w-5 text-amber-600" />
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{service.duration_minutes} {t('minutes')}</span>
                      </div>
                        <div className="flex flex-col">
                          <span className="font-medium text-lg text-gray-900">
                            {typeof service.price === 'number' ? formatLocal(service.price, service.currency || 'USD') : '-'}
                          </span>
                          {(service.service_track === 'clinical' || (isLicensedMode && !service.service_track)) && (
                            <span className="text-xs text-amber-600 font-medium">{t('referencePrice')}</span>
                          )}
                          {typeof service.price === 'number' && (service.currency || 'USD').toUpperCase() !== userCurrency.toUpperCase() && !ratesLoading && (
                            <span className="text-xs text-gray-500">
                              ≈ {new Intl.NumberFormat('en', { style: 'currency', currency: service.currency || 'USD' }).format(service.price)} {service.currency || 'USD'}
                            </span>
                          )}
                        </div>
                    </div>

                    {(() => {
                      const displayDesc = getBilingualValue(i18n.language, (service as any).description_en, (service as any).description_zh, service.description);
                      return displayDesc ? <p className="text-sm text-gray-600 mb-3">{displayDesc}</p> : null;
                    })()}
                    
                    {service.therapy_tags && service.therapy_tags.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex flex-wrap gap-1.5">
                          {service.therapy_tags
                            .sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0))
                            .map((tag) => (
                              <Badge 
                                key={tag.id} 
                                variant={tag.is_primary ? "default" : "secondary"}
                                className={`text-xs ${tag.is_primary ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}`}
                              >
                                {tag.is_primary && <Star className="h-3 w-3 mr-1 fill-current" />}
                                {i18n.language === 'zh' ? tag.therapy_modality.name_cn : tag.therapy_modality.name_en}
                              </Badge>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Age / Consent restriction badges */}
                    {(service.adults_only || service.parental_consent_required) && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {service.adults_only && (
                          <Badge variant="outline" className="text-xs border-orange-300 text-orange-700 bg-orange-50">
                            <ShieldAlert className="h-3 w-3 mr-1" />
                            18+ Only
                          </Badge>
                        )}
                        {service.parental_consent_required && (
                          <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Parental Consent Required
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                {service.formats_available?.includes('virtual') && (
                  <Badge variant="outline">{t('virtual')}</Badge>
                )}
                {service.formats_available?.includes('in_person') && (
                  <Badge variant="outline">{t('inPerson')}</Badge>
                )}
              </div>
                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBookNow(service);
                    }}
                    size="sm"
                    className="ml-3"
                  >
                    {(service.service_track === 'clinical' || (isLicensedMode && !service.service_track)) ? t('requestBooking') : t('bookNow')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Role Access Alert Dialog */}
      <AlertDialog open={showRoleAlert} onOpenChange={setShowRoleAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('accountTypeRestriction')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('practitionerAdminCantBook')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowRoleAlert(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleGoToClientLogin}>{t('goToClientLogin')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PractitionerServices;
